import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import type { PolicyLoanLedgerRow, StandardLoanAmortizationRow, PolicyLoanPayment } from '@/lib/calculationEngine';
import { createFormatter, type Jurisdiction } from '@/lib/formatters';
import { e } from '@/lib/htmlSanitize';

export type ScheduleKey = 'policyLedger' | 'amortization' | 'commitment';
export type ExportFormat = 'csv' | 'xlsx' | 'pdf';

export interface ScheduleExportData {
  policyLedger: PolicyLoanLedgerRow[];
  amortization: StandardLoanAmortizationRow[];
  plannedPayments: PolicyLoanPayment[];
}

export interface ExportMeta {
  policyNumber?: string;
  loanTitle?: string;
  borrowerName?: string;
  jurisdiction: Jurisdiction;
  /** Optional pre-built filename stem, e.g. "FL-RC-0001-2026-06-24-84600". */
  exportFilename?: string;
}

export const SCHEDULE_LABELS: Record<ScheduleKey, string> = {
  policyLedger: 'Policy Loan Ledger',
  amortization: 'Intended Amortization',
  commitment: 'Commitment Schedule',
};

// ---------- Row builders (return AOA = array of arrays with header in row 0) ----------

function buildPolicyLedgerRows(rows: PolicyLoanLedgerRow[]): (string | number)[][] {
  const header = [
    'Date', 'Event', 'Days', 'Rate', 'Interest Accrued',
    'Payment', 'Capitalized Interest', 'Ending Principal',
    'Ending Accrued Interest', 'Total Owing',
  ];
  const body = rows.map(r => [
    r.date,
    r.eventType.replace(/_/g, ' '),
    r.daysInInterval,
    Number((r.annualRateUsed * 100).toFixed(4)),
    Number(r.intervalInterestAccrued.toFixed(2)),
    Number(r.paymentAmount.toFixed(2)),
    Number(r.capitalizedInterest.toFixed(2)),
    Number(r.endingPrincipal.toFixed(2)),
    Number(r.endingAccruedInterest.toFixed(2)),
    Number(r.totalIndebtednessAfterEvent.toFixed(2)),
  ]);
  return [header, ...body];
}

function buildAmortizationRows(rows: StandardLoanAmortizationRow[]): (string | number)[][] {
  const header = ['#', 'Date', 'Payment', 'Interest', 'Principal', 'Remaining Balance'];
  const body = rows.map(r => [
    r.paymentNumber,
    r.paymentDate,
    Number(r.paymentAmount.toFixed(2)),
    Number(r.interestPortion.toFixed(2)),
    Number(r.principalPortion.toFixed(2)),
    Number(r.remainingBalance.toFixed(2)),
  ]);
  return [header, ...body];
}

function buildCommitmentRows(rows: PolicyLoanPayment[]): (string | number)[][] {
  const header = ['#', 'Due Date', 'Payment Amount', 'Paid', 'Date Paid', 'Notes'];
  const body = rows.map((p, i) => [
    i + 1, p.date, Number(p.amount.toFixed(2)), '', '', '',
  ]);
  return [header, ...body];
}

function rowsFor(key: ScheduleKey, data: ScheduleExportData): (string | number)[][] {
  switch (key) {
    case 'policyLedger': return buildPolicyLedgerRows(data.policyLedger);
    case 'amortization': return buildAmortizationRows(data.amortization);
    case 'commitment': return buildCommitmentRows(data.plannedPayments);
  }
}

// ---------- File helpers ----------

function toCsv(aoa: (string | number)[][]): string {
  return aoa.map(row =>
    row.map(cell => {
      const s = String(cell ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')
  ).join('\n');
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function fileSafe(s: string): string {
  return s.replace(/[^a-z0-9_-]+/gi, '_').replace(/^_+|_+$/g, '') || 'export';
}

function baseName(meta: ExportMeta): string {
  if (meta.exportFilename) return fileSafe(meta.exportFilename);
  const parts = [meta.policyNumber, meta.loanTitle, 'schedules'].filter(Boolean).map(s => fileSafe(String(s)));
  return parts.join('_') || 'loan_schedules';
}

// ---------- Public API ----------

export async function exportSchedules(
  selections: ScheduleKey[],
  format: ExportFormat,
  data: ScheduleExportData,
  meta: ExportMeta,
): Promise<void> {
  if (selections.length === 0) return;
  const base = baseName(meta);

  if (format === 'csv') {
    if (selections.length === 1) {
      const key = selections[0];
      const csv = toCsv(rowsFor(key, data));
      downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${base}_${fileSafe(SCHEDULE_LABELS[key])}.csv`);
      return;
    }
    const zip = new JSZip();
    for (const key of selections) {
      zip.file(`${fileSafe(SCHEDULE_LABELS[key])}.csv`, toCsv(rowsFor(key, data)));
    }
    const blob = await zip.generateAsync({ type: 'blob' });
    downloadBlob(blob, `${base}.zip`);
    return;
  }

  if (format === 'xlsx') {
    const wb = XLSX.utils.book_new();
    for (const key of selections) {
      const ws = XLSX.utils.aoa_to_sheet(rowsFor(key, data));
      XLSX.utils.book_append_sheet(wb, ws, SCHEDULE_LABELS[key].slice(0, 31));
    }
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    downloadBlob(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${base}.xlsx`);
    return;
  }

  if (format === 'pdf') {
    const { fmt } = createFormatter(meta.jurisdiction);
    const sections = selections.map(key => {
      const aoa = rowsFor(key, data);
      const [header, ...body] = aoa;
      // numeric column indexes per schedule
      const moneyCols: Record<ScheduleKey, number[]> = {
        policyLedger: [4, 5, 6, 7, 8, 9],
        amortization: [2, 3, 4, 5],
        commitment: [2],
      };
      const cols = moneyCols[key];
      const rowsHtml = body.map(row => `<tr>${row.map((cell, i) => {
        const isNum = cols.includes(i) && typeof cell === 'number';
        const cls = isNum ? ' class="right"' : '';
        const txt = isNum ? fmt(cell as number) : e(String(cell ?? ''));
        return `<td${cls}>${txt}</td>`;
      }).join('')}</tr>`).join('');
      const headerHtml = header.map((h, i) => `<th${cols.includes(i) ? ' class="right"' : ''}>${e(String(h))}</th>`).join('');
      return `
        <h2>${e(SCHEDULE_LABELS[key])}</h2>
        <table><thead><tr>${headerHtml}</tr></thead><tbody>${rowsHtml}</tbody></table>
      `;
    }).join('<div class="page-break"></div>');

    const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>Loan Schedules</title>
<style>
 *{margin:0;padding:0;box-sizing:border-box}
 body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:9.5pt;color:#1a1a2e;padding:0.5in 0.6in;line-height:1.4}
 h1{font-size:15pt;text-align:center;margin-bottom:2pt}
 h2{font-size:11pt;margin:12pt 0 6pt;border-bottom:1px solid #ccc;padding-bottom:3pt}
 .subtitle{text-align:center;font-size:9pt;color:#666;margin-bottom:14pt}
 .meta{display:grid;grid-template-columns:1fr 1fr;gap:3pt 16pt;margin-bottom:14pt;font-size:9pt;border-top:1px solid #ccc;border-bottom:1px solid #ccc;padding:6pt 0}
 table{width:100%;border-collapse:collapse;margin-bottom:12pt;font-size:8.5pt}
 th,td{padding:3pt 4pt;text-align:left;border-bottom:1px solid #e5e5e5}
 th{border-bottom:2px solid #999;font-weight:600;font-size:8pt;text-transform:uppercase;letter-spacing:.3pt}
 td.right,th.right{text-align:right}
 .page-break{page-break-before:always}
 @media print{body{padding:0}}
</style></head><body>
<h1>Family Policy Loan Planner™ — Loan Schedules</h1>
<p class="subtitle">Generated: ${new Date().toLocaleDateString(meta.jurisdiction === 'usa' ? 'en-US' : 'en-CA')}</p>
<div class="meta">
  <div><strong>Policy Number:</strong> ${e(meta.policyNumber || 'N/A')}</div>
  <div><strong>Loan Title:</strong> ${e(meta.loanTitle || 'N/A')}</div>
  ${meta.borrowerName ? `<div><strong>Borrower:</strong> ${e(meta.borrowerName)}</div>` : ''}
  <div><strong>Jurisdiction:</strong> ${meta.jurisdiction === 'usa' ? 'United States' : 'Canada'}</div>
</div>
${sections}
</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
    return;
  }
}